import { useState, useEffect, useCallback } from "react";
import { CircleMarker, Polyline, Popup, useMap } from "react-leaflet";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Sun, Moon, Rocket, Users, Lightning, Atom,
  Eye, Warning, Planet,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ISS icon as a pulsing marker
function ISSMarker({ position }) {
  if (!position) return null;
  return (
    <CircleMarker center={[position.lat, position.lon]} radius={8}
      pathOptions={{ color: "#facc15", fillColor: "#facc15", fillOpacity: 0.9, weight: 3 }}>
      <Popup>
        <div className="font-body text-xs space-y-1">
          <p className="font-heading font-bold text-sm">International Space Station</p>
          <p>Lat: {position.lat.toFixed(4)}, Lon: {position.lon.toFixed(4)}</p>
          <p>Altitude: {position.altitude_km} km</p>
          <p>Speed: {position.velocity_kmh?.toLocaleString()} km/h</p>
        </div>
      </Popup>
    </CircleMarker>
  );
}

function SunMarker({ position }) {
  if (!position) return null;
  return (
    <CircleMarker center={[position.lat, position.lon]} radius={14}
      pathOptions={{ color: "#f59e0b", fillColor: "#fbbf24", fillOpacity: 0.8, weight: 3 }}>
      <Popup>
        <div className="font-body text-xs">
          <p className="font-heading font-bold text-sm">Subsolar Point</p>
          <p>The Sun is directly overhead here</p>
          <p>Lat: {position.lat.toFixed(2)}, Lon: {position.lon.toFixed(2)}</p>
        </div>
      </Popup>
    </CircleMarker>
  );
}

function MoonMarker({ position }) {
  if (!position) return null;
  return (
    <CircleMarker center={[position.lat, position.lon]} radius={10}
      pathOptions={{ color: "#94a3b8", fillColor: "#e2e8f0", fillOpacity: 0.8, weight: 2 }}>
      <Popup>
        <div className="font-body text-xs">
          <p className="font-heading font-bold text-sm">Moon Position</p>
          <p>Phase: {position.phase}</p>
          <p>Illumination: {position.illumination}%</p>
        </div>
      </Popup>
    </CircleMarker>
  );
}

function TerminatorLine({ points }) {
  if (!points || points.length < 2) return null;
  return <Polyline positions={points} pathOptions={{ color: "#f59e0b", weight: 1.5, opacity: 0.5, dashArray: "6 4" }} />;
}

// Map overlay components (rendered inside MapContainer)
export function SpaceMapOverlay({ active }) {
  const [sunMoon, setSunMoon] = useState(null);
  const [iss, setIss] = useState(null);

  useEffect(() => {
    if (!active) return;
    const fetchSunMoon = async () => {
      try {
        const res = await axios.get(`${API}/space/sun-moon`, { withCredentials: true });
        setSunMoon(res.data);
      } catch { /* silent */ }
    };
    const fetchISS = async () => {
      try {
        const res = await axios.get(`${API}/space/iss`, { withCredentials: true });
        setIss(res.data);
      } catch { /* silent */ }
    };
    fetchSunMoon();
    fetchISS();
    const smInterval = setInterval(fetchSunMoon, 60000);
    const issInterval = setInterval(fetchISS, 5000);
    return () => { clearInterval(smInterval); clearInterval(issInterval); };
  }, [active]);

  if (!active) return null;

  return (
    <>
      {sunMoon?.terminator && <TerminatorLine points={sunMoon.terminator} />}
      {sunMoon?.sun && <SunMarker position={sunMoon.sun} />}
      {sunMoon?.moon && <MoonMarker position={sunMoon.moon} />}
      {iss && !iss.error && <ISSMarker position={iss} />}
    </>
  );
}

// Side panel component
export default function SpacePanel({ open, onClose }) {
  const [sunMoon, setSunMoon] = useState(null);
  const [iss, setIss] = useState(null);
  const [astronauts, setAstronauts] = useState(null);
  const [solar, setSolar] = useState(null);
  const [neo, setNeo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/space/sun-moon`, { withCredentials: true }).then(r => setSunMoon(r.data)).catch(() => {}),
      axios.get(`${API}/space/iss`, { withCredentials: true }).then(r => setIss(r.data)).catch(() => {}),
      axios.get(`${API}/space/astronauts`, { withCredentials: true }).then(r => setAstronauts(r.data)).catch(() => {}),
      axios.get(`${API}/space/solar-activity`, { withCredentials: true }).then(r => setSolar(r.data)).catch(() => {}),
      axios.get(`${API}/space/neo`, { withCredentials: true }).then(r => setNeo(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));

    const issInterval = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/space/iss`, { withCredentials: true });
        setIss(r.data);
      } catch { /* silent */ }
    }, 5000);
    return () => clearInterval(issInterval);
  }, [open]);

  if (!open) return null;

  return (
    <motion.div
      initial={{ x: 320, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 320, opacity: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
      className="absolute top-0 right-0 bottom-0 w-80 z-[950] bg-stone-900/95 backdrop-blur-xl border-l border-stone-700 overflow-y-auto"
      data-testid="space-panel"
    >
      {/* Header */}
      <div className="sticky top-0 bg-stone-900/95 backdrop-blur-md px-4 py-3 border-b border-stone-800 flex items-center justify-between z-10">
        <div className="flex items-center gap-2">
          <Rocket size={16} weight="bold" className="text-amber-400" />
          <span className="text-sm font-heading font-semibold text-white">Space Monitor</span>
        </div>
        <button onClick={onClose} className="text-stone-400 hover:text-white p-1 rounded" data-testid="space-panel-close">
          <X size={14} />
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center">
          <div className="w-8 h-8 border-2 border-stone-600 border-t-amber-400 rounded-full animate-spin mx-auto" />
          <p className="text-xs text-stone-500 mt-3">Loading space data...</p>
        </div>
      ) : (
        <div className="p-4 space-y-5">
          {/* ISS Tracker */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-md bg-yellow-500/20 flex items-center justify-center">
                <Rocket size={12} weight="bold" className="text-yellow-400" />
              </div>
              <h3 className="text-xs uppercase tracking-wider text-stone-400 font-body">ISS Live Position</h3>
            </div>
            {iss && !iss.error ? (
              <div className="bg-stone-800/60 rounded-xl p-3 border border-stone-700/50 space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-[10px] text-stone-500">Latitude</span>
                  <span className="text-xs text-white font-mono">{iss.lat.toFixed(4)}°</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[10px] text-stone-500">Longitude</span>
                  <span className="text-xs text-white font-mono">{iss.lon.toFixed(4)}°</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[10px] text-stone-500">Altitude</span>
                  <span className="text-xs text-white">{iss.altitude_km} km</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[10px] text-stone-500">Speed</span>
                  <span className="text-xs text-white">{iss.velocity_kmh?.toLocaleString()} km/h</span>
                </div>
                <p className="text-[9px] text-stone-600 pt-1">{iss.description}</p>
                <div className="flex items-center gap-1 pt-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-[9px] text-green-400">Tracking live — updates every 5s</span>
                </div>
              </div>
            ) : <p className="text-xs text-stone-600">ISS data unavailable</p>}
          </section>

          {/* Astronauts */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-md bg-sky-500/20 flex items-center justify-center">
                <Users size={12} weight="bold" className="text-sky-400" />
              </div>
              <h3 className="text-xs uppercase tracking-wider text-stone-400 font-body">
                Astronauts in Space ({astronauts?.count || 0})
              </h3>
            </div>
            {astronauts?.people?.length > 0 ? (
              <div className="space-y-1">
                {astronauts.people.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 bg-stone-800/40 rounded-lg px-3 py-1.5 border border-stone-800/60">
                    <div className="w-5 h-5 rounded-full bg-sky-500/20 flex items-center justify-center text-[8px] text-sky-400 font-bold">
                      {p.name?.[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] text-white truncate">{p.name}</p>
                      <p className="text-[9px] text-stone-500">{p.craft}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-stone-600">No data</p>}
          </section>

          {/* Sun & Moon */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-md bg-amber-500/20 flex items-center justify-center">
                <Sun size={12} weight="bold" className="text-amber-400" />
              </div>
              <h3 className="text-xs uppercase tracking-wider text-stone-400 font-body">Sun & Moon</h3>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-stone-800/60 rounded-xl p-3 border border-stone-700/50">
                <Sun size={18} className="text-amber-400 mb-1" />
                <p className="text-[10px] text-stone-500">Subsolar Point</p>
                <p className="text-xs text-white font-mono">{sunMoon?.sun?.lat.toFixed(2)}°, {sunMoon?.sun?.lon.toFixed(2)}°</p>
              </div>
              <div className="bg-stone-800/60 rounded-xl p-3 border border-stone-700/50">
                <Moon size={18} className="text-slate-300 mb-1" />
                <p className="text-[10px] text-stone-500">{sunMoon?.moon?.phase}</p>
                <p className="text-xs text-white">{sunMoon?.moon?.illumination}% lit</p>
              </div>
            </div>
            <p className="text-[9px] text-stone-600 mt-1.5">Gold dashed line on map = day/night terminator (sunrise/sunset boundary)</p>
          </section>

          {/* Solar Activity */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-md bg-orange-500/20 flex items-center justify-center">
                <Lightning size={12} weight="bold" className="text-orange-400" />
              </div>
              <h3 className="text-xs uppercase tracking-wider text-stone-400 font-body">Solar Activity (7 days)</h3>
            </div>
            <div className="space-y-2">
              <div className="bg-stone-800/60 rounded-xl p-3 border border-stone-700/50">
                <p className="text-[10px] text-stone-500">Solar Flares</p>
                <p className="text-lg font-heading font-semibold text-orange-400">{solar?.flares?.length || 0}</p>
                {solar?.flares?.slice(0, 3).map((f, i) => (
                  <p key={i} className="text-[10px] text-stone-500 truncate">
                    Class {f.class} — {f.begin?.split("T")[0]} {f.source && `(${f.source})`}
                  </p>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-stone-800/60 rounded-xl p-3 border border-stone-700/50">
                  <p className="text-[10px] text-stone-500">Geo Storms</p>
                  <p className="text-lg font-heading font-semibold text-violet-400">{solar?.storms?.length || 0}</p>
                </div>
                <div className="bg-stone-800/60 rounded-xl p-3 border border-stone-700/50">
                  <p className="text-[10px] text-stone-500">CMEs</p>
                  <p className="text-lg font-heading font-semibold text-sky-400">{solar?.cme?.length || 0}</p>
                </div>
              </div>
              <p className="text-[9px] text-stone-600">Data from NASA DONKI — Space Weather Database of Notifications, Knowledge, and Information</p>
            </div>
          </section>

          {/* Near-Earth Objects */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-md bg-red-500/20 flex items-center justify-center">
                <Planet size={12} weight="bold" className="text-red-400" />
              </div>
              <h3 className="text-xs uppercase tracking-wider text-stone-400 font-body">
                Near-Earth Objects Today ({neo?.count || 0})
              </h3>
            </div>
            {neo?.objects?.length > 0 ? (
              <div className="space-y-1.5">
                {neo.objects.slice(0, 8).map((obj) => (
                  <div key={obj.id} className={`bg-stone-800/40 rounded-lg px-3 py-2 border ${obj.hazardous ? "border-red-700/40" : "border-stone-800/60"}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] text-white truncate flex-1">{obj.name}</p>
                      {obj.hazardous && (
                        <span className="text-[8px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded font-bold ml-1">HAZARDOUS</span>
                      )}
                    </div>
                    <div className="flex gap-3 mt-1 text-[9px] text-stone-500">
                      <span>{obj.diameter_m}m dia</span>
                      <span>{(obj.miss_distance_km / 1e6).toFixed(2)}M km</span>
                      <span>{(obj.velocity_kmh / 1000).toFixed(1)}k km/h</span>
                    </div>
                  </div>
                ))}
                <p className="text-[9px] text-stone-600">Data from NASA NeoWs — sorted by closest approach distance</p>
              </div>
            ) : <p className="text-xs text-stone-600">No NEOs detected today</p>}
          </section>
        </div>
      )}
    </motion.div>
  );
}
